import { FormControl, FormGroup, styled } from '@mui/material';
import {
  Alert,
  Dialog,
  DialogActionsWithButtons,
  DialogContent,
  toast,
} from '@side/pantry';
import { useCallback, useEffect, useState } from 'react';
import { SideSupport } from 'src/components/SideSupport/SideSupport';
import type { GQLInteractionType } from 'src/generated/graph-api';
import { isAbortError } from '@/common/hooks/runners/utils';
import type { InteractionDocument } from '@/common/types/interaction';
import { TrackDocumentsEvent } from '$analytics/useTrackAnalytics/domains/documents/events';
import { useTrackDocsAnalytics } from '$analytics/useTrackAnalytics/domains/documents/useTrackDocsAnalytics';
import { EnvelopeStatus } from '../../views/SentEvelopesSection/enums';
import { EmptyState } from '../EmptyState';
import { EnvelopeGroup } from './components/EnvelopeGroup';
import { useGetOfferEnvelopes } from './useGetOfferEnvelopes';

const Options = styled(FormGroup)`
  display: grid;
  grid-gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  grid-template-columns: repeat(auto-fill, minmax(500px, auto));
  ${({ theme }) => theme.breakpoints.only('xs')} {
    grid-template-columns: auto;
  }
`;
export interface Props {
  interactionId: string;
  interactionType: GQLInteractionType;
  isUpdatingOffer: boolean;
  open: boolean;
  generateOffer: (documentIds: string[]) => Promise<void>;
  handleClose: (analyticsInfo: string) => void;
}

export const GenerateOfferModal = ({
  interactionId,
  interactionType,
  isUpdatingOffer,
  open,
  generateOffer,
  handleClose,
}: Props) => {
  const trackEvent = useTrackDocsAnalytics();
  const [showUnsignedWarningAlert, setShowUnsignedWarningAlert] =
    useState(false);
  const [selectedDocumentsMap, setSelectedDocumentsMap] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [selectedDocumentsForOffer, setSelectedDocumentsForOffer] = useState<
    string[]
  >([]);

  const { envelopes, loading } = useGetOfferEnvelopes({
    id: interactionId,
    type: interactionType,
    open,
  });

  const getAnalyticsInfo = useCallback(() => {
    const analyticsInfo: { envelopeId: string; documentIds: string[] }[] = [];
    Object.keys(selectedDocumentsMap).forEach((envelopeId) => {
      analyticsInfo.push({
        envelopeId,
        documentIds: Object.keys(selectedDocumentsMap[envelopeId] || {}).filter(
          (docId) => selectedDocumentsMap[envelopeId][docId] === true,
        ),
      });
    });

    return JSON.stringify(analyticsInfo);
  }, [selectedDocumentsMap]);

  const handleGenerateOffer = async () => {
    if (!selectedDocumentsForOffer.length) return;

    const analyticsInfo = getAnalyticsInfo();
    const clickEvent = isUpdatingOffer
      ? TrackDocumentsEvent.ClickUpdateOfferModal
      : TrackDocumentsEvent.ClickGenerateOfferModal;

    handleClose(analyticsInfo);

    try {
      await generateOffer(selectedDocumentsForOffer);
      trackEvent(clickEvent, {
        envelope_ids: analyticsInfo,
        success: true,
      });
    } catch (error) {
      if (isAbortError(error)) return;
      toast(
        'error',
        <>
          Offer Generation failed. Try again or contact&nbsp;
          <SideSupport />
          &nbsp;for assistance.
        </>,
        { showCloseButton: true },
      );
      trackEvent(clickEvent, {
        envelope_ids: analyticsInfo,
        success: false,
      });
    }
  };

  const onDocumentSelected = useCallback(
    ({
      id,
      checked,
      envelopeId,
    }: {
      id: string;
      checked: boolean;
      envelopeId: string;
    }) => {
      setSelectedDocumentsMap({
        ...selectedDocumentsMap,
        [envelopeId]: { ...selectedDocumentsMap[envelopeId], [id]: checked },
      });
      trackEvent(TrackDocumentsEvent.ToggleDocumentForOffer, {
        document_id: id,
        result: checked ? 'select' : 'unselect',
      });
    },
    [selectedDocumentsMap, trackEvent],
  );

  const onEnvelopeSelected = useCallback(
    ({
      checked,
      envelopeId,
      envelopeDocs,
    }: {
      checked: boolean;
      envelopeId: string;
      envelopeDocs: (InteractionDocument | null)[];
    }) => {
      trackEvent(TrackDocumentsEvent.ToggleEnvelopeForOffer, {
        envelope_id: envelopeId,
        result: checked ? 'select' : 'unselect',
      });

      envelopeDocs.forEach((document) => {
        if (!document?.id) {
          return;
        }

        setSelectedDocumentsMap((prevState) => {
          return {
            ...prevState,
            [envelopeId]: {
              ...prevState[envelopeId],
              [document.id || '']: checked,
            },
          };
        });
      });
    },
    [trackEvent],
  );

  useEffect(() => {
    setSelectedDocumentsForOffer([]);
    Object.values(selectedDocumentsMap).forEach((envelope) => {
      Object.entries(envelope).forEach(([documentId, checked]) => {
        if (checked === true) {
          setSelectedDocumentsForOffer((prevState) => [
            ...prevState,
            documentId,
          ]);
        }
      });
    });
  }, [selectedDocumentsMap]);

  useEffect(() => {
    const unsignedDocsSelected = Object.keys(selectedDocumentsMap).filter(
      (envelopeId) =>
        Object.keys(selectedDocumentsMap[envelopeId]).some((docId) => {
          return (
            selectedDocumentsMap[envelopeId][docId] === true &&
            envelopes?.find((env) => env?.id === envelopeId)?.status !==
              EnvelopeStatus.completed
          );
        }),
    );

    if (unsignedDocsSelected.length) {
      setShowUnsignedWarningAlert(true);
    } else {
      setShowUnsignedWarningAlert(false);
    }
  }, [selectedDocumentsMap, envelopes]);

  return (
    <Dialog aria-labelledby="dialog-title" open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          '& .MuiFormLabel-root': { mt: 3 },
        }}
        titleSx={{ pb: 3 }}
        onClose={() => handleClose(getAnalyticsInfo())}
        title="Select Docs for Offer"
      >
        {showUnsignedWarningAlert ? (
          <Alert severity="warning" sx={{ mb: 3, mt: 0 }}>
            You are about to generate an offer with unsigned documents
          </Alert>
        ) : null}
        {loading || !envelopes?.length ? (
          <EmptyState loading={loading} message="No envelopes were found." />
        ) : (
          <>
            <FormControl
              component="fieldset"
              sx={{ width: '100%', display: 'none' }}
            />
            <Options aria-label="envelope list">
              {envelopes.map((envelope, index) => {
                if (!envelope) {
                  return null;
                }
                const { id } = envelope;
                if (!id) {
                  return null;
                }

                const selectedDocuments = Object.keys(
                  selectedDocumentsMap[envelope.id] || {},
                ).filter((docId) => {
                  return selectedDocumentsMap[envelope.id][docId] === true;
                });

                return (
                  <EnvelopeGroup
                    key={envelope.id}
                    envelope={envelope}
                    isFirstEnvelopeInList={index === 0}
                    selectedDocuments={selectedDocuments}
                    onDocumentSelected={onDocumentSelected}
                    onEnvelopeSelected={onEnvelopeSelected}
                  />
                );
              })}
            </Options>
          </>
        )}
      </DialogContent>
      <DialogActionsWithButtons
        CancelButtonProps={{ onClick: () => handleClose(getAnalyticsInfo()) }}
        SubmitButtonProps={{
          defaultText: 'Generate',
          disabled: !selectedDocumentsForOffer.length,
          onClick: handleGenerateOffer,
        }}
      />
    </Dialog>
  );
};
